import { CompetenceBaseWithChildrenEx } from "@/server/backend-api";
import { useMemo } from "react";
import { GetServerSidePropsContext } from "next";
import { getAuthStatus } from "@/server/getAuthStatus";
import { getMotivationScanAnswersIfExists } from "@/server/backend-api/motivationScan";
import { getMotivationScanAnswersFromCookie } from "@/utils/cookies/cookies";
import { CompetenceRelationshipMap } from "@/utils/competenceTree";

export enum MotivationScanLike {
  Like = "like",
  Dislike = "dislike",
}

export interface MotivationScanAnswer {
  competenceId: string;
  likeOrDislike?: MotivationScanLike;
}

export interface MotivationScanAnswers {
  [competenceId: string]: MotivationScanAnswer | undefined;
}

export function sortCompetencesByRatioOfLikedChildren(
  competences: CompetenceBaseWithChildrenEx[],
  answers: MotivationScanAnswers
): CompetenceBaseWithChildrenEx[] {
  const _copy = [...competences];
  _copy.sort((left, right) => {
    const leftRatio =
      getNrOfSelectedChildren(left, answers) / left.children.length;
    const rightRatio =
      getNrOfSelectedChildren(right, answers) / right.children.length;
    return leftRatio - rightRatio;
  });
  return _copy.reverse();
}

function getNrOfSelectedChildren(
  competence: CompetenceBaseWithChildrenEx,
  answers: MotivationScanAnswers
) {
  let nrOfSelectedChildren = 0;
  for (const child of competence.children) {
    const answer = answers[child.id];
    if (
      answer?.likeOrDislike === MotivationScanLike.Like ||
      getNrOfSelectedChildren(child, answers) > 0
    ) {
      nrOfSelectedChildren++;
    }
  }
  return nrOfSelectedChildren;
}

export class MotivationScanAnswersWithStats {
  _answers: MotivationScanAnswers | null;
  _relationshipMap: CompetenceRelationshipMap;

  constructor(
    answers: MotivationScanAnswers | null,
    relationshipMap: CompetenceRelationshipMap
  ) {
    this._relationshipMap = relationshipMap;
    this._answers = answers;
  }

  isLiked(subtopicId: string): boolean {
    if (this._answers === null) {
      return false;
    }
    const answer = this._answers[subtopicId];
    if (answer?.likeOrDislike === MotivationScanLike.Like) {
      return true;
    }
    const childrenIds = this._relationshipMap.getChildren(subtopicId);
    for (const childId of childrenIds) {
      if (this.isLiked(childId)) {
        return true;
      }
    }
    return false;
  }
}

export function useMotivationScanAnswers(answers: MotivationScanAnswers) {
  return useMemo(() => {
    function isLikedOrChildrenLiked(
      subtopic: CompetenceBaseWithChildrenEx
    ): boolean {
      const thisIsLiked =
        answers[subtopic.id]?.likeOrDislike === MotivationScanLike.Like;
      if (thisIsLiked) {
        return true;
      }
      return subtopic.children.some(isLikedOrChildrenLiked);
    }

    function getLikedChildrenCount(competence: CompetenceBaseWithChildrenEx) {
      return competence.children.filter(isLikedOrChildrenLiked).length;
    }

    function isAllChildrenLiked(competence: CompetenceBaseWithChildrenEx) {
      return competence.children.every(isLikedOrChildrenLiked);
    }

    return {
      isLikedOrChildrenLiked,
      getLikedChildrenCount,
      isAllChildrenLiked,
    };
  }, [answers]);
}

export async function getSSRMotivationScan(
  context: GetServerSidePropsContext
): Promise<MotivationScanAnswers | null> {
  const auth = getAuthStatus(context.req);
  const answers = auth.isAuthenticated
    ? await getMotivationScanAnswersIfExists(context.req)
    : getMotivationScanAnswersFromCookie(context.req.cookies);
  return answers;
}
